import { nullable } from '@ipfabric/utils/fp/nullable';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import type { GraphState } from 'components/Graph/GraphState';
import {
  type EmptyPathLookupResultCause,
  isEmptyPathLookupResultCause,
} from 'components/Graph/model/pathLookup';
import { selectPathLookupMetadata } from 'components/Graph/model/selectors/graphMetadata/selectPathLookupMetadata';
import { useLocale } from 'containers/App/LocaleContext';
import { useLogError } from 'containers/ErrorBoundary/useLogError';
import { array } from 'fp-ts';
import { flow } from 'fp-ts/lib/function';
import { useMemo } from 'react';
import type { FC } from 'react';
import { createSelector } from 'reselect';
import { createScopedSelector } from 'utils/react/scopedSelector';

const selectEmptyPathLookupCauses: (
  s: GraphState,
) => EmptyPathLookupResultCause[] = createScopedSelector(() =>
  createSelector(
    selectPathLookupMetadata,
    flow(
      nullable.chain(
        flow(
          prop('eventsSummary', 'flags'),
          array.filter(isEmptyPathLookupResultCause),
        ),
      ),
      nullable.getOrElse((): EmptyPathLookupResultCause[] => array.empty),
    ),
  ),
);

export const FailedPathlookup: FC = () => {
  const msgFailedPl = useLocale().canvasGraph.emptyGraph.failedPathlookup;
  const logError = useLogError();
  const ctxProps = useGraphContextSelectors({
    causes: selectEmptyPathLookupCauses,
  });
  const messagesByState = useMemo(() => {
    switch (ctxProps.causes.length) {
      case 0:
        // When there are no possible cause flags, it's unexpected state - show special error,
        // because all empty path lookup results should be caused by one or more of specified causes.
        logError('Empty pathlookup result with no known cause', 'GRAPH');
        return {
          title: msgFailedPl.title,
          description: msgFailedPl.noRelevantFlag,
        };
      case 1:
        // When there is a single cause, show it as the main error message.
        // Fixed noUncheckedIndexedAccess NIM-19408
        return msgFailedPl.emptyPathLookupCauses[ctxProps.causes[0]!];
      default:
        // When there are multiple causes, show generic error and list the causes.
        return {
          title: msgFailedPl.title,
          description: msgFailedPl.description,
        };
    }
  }, [ctxProps.causes, msgFailedPl]);
  return (
    <>
      <p className='text-bold text-uppercase text-danger'>
        <i className='fa fa-exclamation-circle mr-2.5' />
        {messagesByState.title}
      </p>
      <p>{messagesByState.description}</p>
      {ctxProps.causes.length > 1 && (
        <ol>
          {ctxProps.causes.map((cause) => (
            <li key={cause}>
              {msgFailedPl.emptyPathLookupCauses[cause].title}
              {'. '}
              {msgFailedPl.emptyPathLookupCauses[cause].description}
            </li>
          ))}
        </ol>
      )}
    </>
  );
};
